import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../lib/i18next";
import { APPROVAL_REVIEWER_MAX_REACHED } from "../../i18n/keysTranslations";
import { MAX_APPROVAL_REVIEWERS } from "./approvalReviewersUtils";
import ApprovalAddReviewerButton from "./ApprovalAddReviewerButton";
import ApprovalReviewerRow from "./ApprovalReviewerRow";

const REVIEWERS_LIST_MAX_HEIGHT = 280;
const REVIEWERS_LIST_SCROLL_PADDING_BOTTOM = 16;

const ApprovalReviewersList = ({
  reviewers = [],
  approvalType = "content",
  approvalID = "",
  onCopied = () => {},
  onAddReviewer = () => {},
  onReviewerAdded = () => {},
  onPrimaryReviewerEmailSent = () => {},
}) => {
  const { t } = useTranslationApp();
  const canAddReviewer = reviewers.length < MAX_APPROVAL_REVIEWERS;
  const scrollContainerRef = useRef(null);
  const previousReviewersLengthRef = useRef(0);

  const scrollToLatestReviewer = () => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (reviewers.length > previousReviewersLengthRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToLatestReviewer();
        });
      });
    }

    previousReviewersLengthRef.current = reviewers.length;
  }, [reviewers.length]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          maxHeight: REVIEWERS_LIST_MAX_HEIGHT,
          overflowY: "auto",
          pr: 0.5,
          pb: 2,
          scrollPaddingBottom: REVIEWERS_LIST_SCROLL_PADDING_BOTTOM,
        }}
      >
        {reviewers.map((reviewer, index) => {
          const isPrimary = index === 0;

          return (
            <ApprovalReviewerRow
              key={reviewer.localID}
              reviewerNumber={index + 1}
              approvalType={approvalType}
              approvalID={approvalID}
              approvalUrl={reviewer.url}
              reviewerID={reviewer.reviewerID}
              isPrimary={isPrimary}
              showReviewerLabel
              defaultEmail={reviewer.email}
              isEmailSent={reviewer.isEmailSent}
              onReviewerEmailSent={
                isPrimary ? onPrimaryReviewerEmailSent : undefined
              }
              onCopied={onCopied}
              onReviewerAdded={({ reviewerID, url, email }) =>
                onReviewerAdded({
                  localID: reviewer.localID,
                  reviewerID,
                  url,
                  email,
                })
              }
            />
          );
        })}
      </Box>

      {canAddReviewer ? (
        <ApprovalAddReviewerButton onClick={onAddReviewer} sx={{ mt: 2 }} />
      ) : (
        <Typography variant="caption" color="text.secondary">
          {t(APPROVAL_REVIEWER_MAX_REACHED)}
        </Typography>
      )}
    </Box>
  );
};

export default ApprovalReviewersList;
